// UserForm.jsx

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button, Box, Typography, List } from '@mui/material';
import { ListSubheader, ListItem, ListItemText } from '@mui/material';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { getUserModules } from '../../../api/userService';
import AccessLevelSelect from '../../common/controls/AccessLevelSelect';
import { useUpdateUserModules } from '../../../hooks/admin/useUpdateUserModules';

export default function UserModules({ userOffice = null, open, onClose }) {
  const { t } = useTranslation('common'); 
  const { showSnackbar } = useSnackbar();

  const [modules, setModules] = useState(null);

  const [changes, setChanges] = useState([]);

  const handleCancel = () => {
    onClose();
    setChanges([]);
  };

  const fetchModules = async () => {
    try {
      const response = await getUserModules(userOffice.user.id, userOffice.office.id);
      setModules(response.data);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const onAccessLevelSelect = (value) => {
    const obj = {
      office_id: userOffice.office.id,
      app_sub_module_id: value.split(':')[0],
      user_id: userOffice.user.id,
      access_level: value.split(':')[1]
    };

    // Use the setter to update state immutably
    setChanges((prevChanges) => {
      // Remove any existing object with the same app_sub_module_id
      const filtered = prevChanges.filter(
        (item) => item.app_sub_module_id !== obj.app_sub_module_id
      );

      // Add the new object
      return [...filtered, obj];
    });

    // If you want to see the latest changes immediately, log inside the setter
    // or use useEffect to watch `changes`
  };

  // useEffect(() => {
  //   console.log("changes", changes);
  // }, [changes]);

  const onUserModulesUpdateSuccess = () => {
    showSnackbar(t('user_modules.messages.update_success'), "success");
    handleCancel();
    fetchModules();
  };

  const { update, updating } = useUpdateUserModules(onUserModulesUpdateSuccess);

  const handleSave = async () => {
    try {
      await update(changes);
    } catch (err) {
      console.log("err.response", err.response);
      showSnackbar(t('user_modules.messages.update_fail'), "error");
    }
  };

  useEffect(() => {
    if (open) fetchModules();
  }, [open]);

  return (
    <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
    <DialogTitle>
      <>
        <Typography fontSize={20} fontWeight={700}>Modules Access</Typography>
        <Typography fontSize={20} fontWeight={700}>{userOffice?.user?.name} </Typography>
      </>
    </DialogTitle>

    <DialogContent dividers>
      {userOffice?.type === 'administrator' ? (
        <Typography>
          User is an administrator, which has full access to all modules.
        </Typography>
      ) : (
        <List>
          {modules?.map((module) => (
            <React.Fragment key={module.id}>
              {/* Main Module */}
              <ListSubheader sx={{ pl: 0, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'left' }}>
                  <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {module.name}
                  </Typography>
                </Box>
              </ListSubheader>

              {/* SubModules */}
              {module.subModules.map((sub) => (
                <React.Fragment key={sub.id}>
                  <ListItem key={sub.id} sx={{ pl: 0, py: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" component="span">
                          {sub.name} :
                        </Typography>
                      }
                      primaryTypographyProps={{
                        noWrap: true,
                        style: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                      secondary={
                        <AccessLevelSelect
                          sx={{ m: 0 }}
                          value={`${sub.id}:${sub.access_level || 0}`}
                          onChange={onAccessLevelSelect}
                        />
                      }
                      secondaryTypographyProps={{ component: 'div' }} 
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </List>
      )}
    </DialogContent>


    <DialogActions>
      <Button 
        onClick={handleSave}
        disabled={changes.length === 0}
      >
        Save
      </Button>
      <Button onClick={handleCancel}>Cancel</Button>
    </DialogActions>
    </Dialog>
  );
}